import { Button } from "@/components/ui/button";
import { CheckCircle2, Mail, X } from "lucide-react";
import { useState, useEffect } from "react";

interface SuccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    message: string;
    buttonText?: string;
    buttonAction?: () => void;
    showEmailIcon?: boolean;
    showResendButton?: boolean;
    resendEmail?: () => Promise<void>;
}

export default function SuccessModal({
    isOpen,
    onClose,
    title,
    message,
    buttonText = "Entendi",
    buttonAction,
    showEmailIcon = false,
    showResendButton = false,
    resendEmail
}: SuccessModalProps) {

    const [countdown, setCountdown] = useState(59);
    const [canResend, setCanResend] = useState(false);
    const [resending, setResending] = useState(false);



    useEffect(() => {
        if (!showResendButton || !isOpen) return;

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    setCanResend(true);
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [showResendButton, isOpen]);

    const handleResend = async () => {
        if (!resendEmail || !canResend) return;

        setResending(true);
        try {
            await resendEmail();
            setCountdown(59);
            setCanResend(false);
        } catch (error) {
            console.error('Erro ao reenviar:', error);
        } finally {
            setResending(false);
        }
    };

    // Fechar modal com ESC
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-card border border-border/50 shadow-2xl rounded-2xl p-8 max-w-md w-full mx-4 animate-in fade-in zoom-in-95 duration-300">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 p-2 hover:bg-muted rounded-full transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Content */}
                <div className="text-center">

                    {/* Icon */}
                    <div className="mb-6">
                        <div className="w-16 h-16 mx-auto bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                            {showEmailIcon ? (
                                <Mail className="w-8 h-8 text-white" />
                            ) : (
                                <CheckCircle2 className="w-8 h-8 text-white" />
                            )}
                        </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-bold mb-4">
                        <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                            {title}
                        </span>
                    </h2>

                    {/* Message */}
                    <p className="text-muted-foreground mb-8 leading-relaxed">
                        {message}
                    </p>

                    {/* Action Button */}
                    <Button
                        onClick={buttonAction || onClose}
                        className="w-full h-11 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 transition-all duration-300"
                    >
                        {buttonText}
                    </Button>
                    {/* Botão de reenvio */}
                    {showResendButton && (
                        <Button
                            onClick={handleResend}
                            disabled={!canResend || resending}
                            variant="ghost"
                            className="w-full mt-3 text-sm"
                        >
                            {resending ? (
                                "Reenviando..."
                            ) : canResend ? (
                                "Não recebeu? Reenviar email"
                            ) : (
                                `Reenviar disponível em ${countdown}s`
                            )}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}